/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx, keyframes } from '@emotion/react';

import * as React from 'react';
import { memo, useCallback, useMemo } from 'react';

import { typography2 } from '@coursera/cds-core';
import type { ClickButton } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import SparkleIcon from 'bundles/unified-common/components/coach-on-discovery/SparkleIcon';

import _t from 'i18n!nls/search-common';

/**
 * Gradient colors from the AI Highlight Treatments spec:
 * 0% → #1CFFBA | 20% → #0FF0FF | 54% → #4B95FF | 100% → #1E72EB
 */
const AI_GRADIENT = `linear-gradient(
  90deg,
  #1CFFBA 0%,
  #0FF0FF 20%,
  #4B95FF 54%,
  #1E72EB 100%
)`;

/**
 * Conic gradient for the alive variant — a visible segment (~180deg)
 * that rotates around the pill like a snake travelling the border.
 */
const AI_CONIC_GRADIENT = `conic-gradient(
  transparent 0deg,
  #1CFFBA 30deg,
  #0FF0FF 80deg,
  #4B95FF 130deg,
  #1E72EB 180deg,
  transparent 210deg,
  transparent 360deg
)`;

export type AiPromptPillSize = 'small' | 'medium';

export type AiPromptPillVariant = 'white' | 'alive';

export type AiPromptPillProps = {
  label: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  isSelected?: boolean;
  disabled?: boolean;
  size?: AiPromptPillSize;
  variant?: AiPromptPillVariant;
  icon?: React.ReactNode | null;
  additionalEventingData?: Omit<Partial<ClickButton>, 'button'>;
  ariaLabel?: string;
};

const gradientSpin = keyframes`
  from {
    transform: translate(-50%, -50%) rotate(0deg);
  }
  to {
    transform: translate(-50%, -50%) rotate(360deg);
  }
`;

const gradientBorderPseudo = `
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 20px;
  background: ${AI_GRADIENT};
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask-composite: exclude;
  pointer-events: none;
`;

const styles = {
  base: css`
    ${typography2.bodySecondary}
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    border-radius: 20px;
    cursor: pointer;
    white-space: nowrap;
    color: var(--cds-color-neutral-primary);
    transition: background-color 0.15s ease, border-color 0.15s ease;
    outline: none;
  `,

  small: css`
    height: 32px;
    padding: var(--cds-spacing-50) var(--cds-spacing-150);
  `,

  medium: css`
    height: 40px;
    padding: var(--cds-spacing-100) var(--cds-spacing-200);
  `,
  white: css`
    background-color: var(--cds-color-neutral-background-primary);
    border: 1px solid var(--cds-color-grey-100);
    isolation: isolate;

    &:hover,
    &:focus-visible {
      background-color: var(--cds-color-neutral-background-primary-weak);
    }

    &:active {
      background-color: var(--cds-color-neutral-background-primary-weak);
      border-color: transparent;
    }

    &:active::before {
      ${gradientBorderPseudo}
      inset: -1px;
      border-radius: 21px;
      padding: 1px;
    }

    &:focus-visible {
      outline: 1px solid var(--cds-color-emphasis-primary-stroke-default);
      outline-offset: 1px;
    }
  `,

  alive: css`
    background-color: transparent;
    border: none;
    overflow: hidden;
    isolation: isolate;

    &::before {
      content: '';
      position: absolute;
      width: 200%;
      aspect-ratio: 1;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: ${AI_CONIC_GRADIENT};
      animation: ${gradientSpin} 3s linear infinite;
      z-index: -2;
      pointer-events: none;
    }

    &::after {
      content: '';
      position: absolute;
      inset: 1px;
      border-radius: 19px;
      background-color: var(--cds-color-neutral-background-primary);
      z-index: -1;
      pointer-events: none;
    }

    &:hover::after,
    &:active::after,
    &:focus-visible::after {
      background-color: var(--cds-color-neutral-background-primary-weak);
    }

    &:focus-visible {
      outline: 1px solid var(--cds-color-emphasis-primary-stroke-default);
      outline-offset: 1px;
    }

    @media (prefers-reduced-motion: reduce) {
      &::before {
        animation: none;
      }
    }
  `,

  whiteSelected: css`
    border-color: transparent;

    &::before {
      ${gradientBorderPseudo}
      inset: -1px;
      border-radius: 21px;
      padding: 2px;
    }
  `,

  aliveSelected: css`
    &::after {
      inset: 2px;
      border-radius: 18px;
    }
  `,

  disabled: css`
    cursor: not-allowed;
    color: var(--cds-color-neutral-disabled-strong);

    &:hover,
    &:focus-visible {
      background-color: var(--cds-color-neutral-background-primary);
    }
  `,

  whiteDisabled: css`
    border-color: var(--cds-color-neutral-disabled-strong);

    &:hover,
    &:focus-visible,
    &:active {
      border-color: var(--cds-color-neutral-disabled-strong);
    }

    &:active::before {
      content: none;
    }
  `,

  aliveDisabled: css`
    &::before {
      background: var(--cds-color-neutral-disabled-strong);
      animation: none;
    }

    &:hover::after,
    &:focus-visible::after {
      background-color: var(--cds-color-neutral-background-primary);
    }
  `,

  iconWrapper: css`
    display: flex;
    align-items: center;
    flex-shrink: 0;
  `,

  label: css`
    overflow: hidden;
    text-overflow: ellipsis;
  `,
};

export const AiPromptPill = memo(function AiPromptPill({
  label,
  onClick,
  isSelected = false,
  disabled = false,
  size = 'small',
  variant = 'white',
  icon,
  additionalEventingData,
  ariaLabel,
}: AiPromptPillProps) {
  const track = useTracker();

  const defaultIcon = <SparkleIcon size={size} />;
  const resolvedIcon = icon === null ? null : icon ?? defaultIcon;

  const buttonCss = useMemo(
    () => [
      styles.base,
      size === 'small' ? styles.small : styles.medium,
      variant === 'white' && styles.white,
      variant === 'alive' && styles.alive,
      isSelected && variant === 'white' && styles.whiteSelected,
      isSelected && variant === 'alive' && styles.aliveSelected,
      disabled && styles.disabled,
      disabled && variant === 'white' && styles.whiteDisabled,
      disabled && variant === 'alive' && styles.aliveDisabled,
    ],
    [size, variant, isSelected, disabled]
  );

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      track('click_button', {
        button: {
          name: 'ai_prompt_pill',
        },
        ...additionalEventingData,
      });
      onClick?.(e);
    },
    [track, additionalEventingData, onClick]
  );

  const resolvedAriaLabel = ariaLabel ?? _t('#{label}, AI prompt', { label });

  return (
    <button
      type="button"
      onClick={handleClick}
      css={buttonCss}
      data-testid="ai-prompt-pill"
      disabled={disabled}
      aria-label={resolvedAriaLabel}
      aria-pressed={isSelected}
    >
      {resolvedIcon && (
        <span css={styles.iconWrapper} aria-hidden="true">
          {resolvedIcon}
        </span>
      )}
      <span css={styles.label}>{label}</span>
    </button>
  );
});

export default AiPromptPill;
